import { useState, useEffect, useRef } from "react";
import { lsGet, lsSet, ssGet, ssSet, ssRemove } from "../utils/storage.js";
import { now } from "../utils/slug.js";
import { SHOP_ITEMS } from "../utils/seed.js";
import { migrateLocalStorageToDb } from "../services/migration.js";
import { getCandidates, getAdminPersonas, getCandidateBySlug, getCandidate } from "../services/candidates.js";
import { upsertVoter } from "../services/auth.js";
import { getXpState } from "../services/xp.js";
import { getRewards } from "../services/rewards.js";
import { getApplication } from "../services/persona-owner.js";
import { recordConsents, POLICY_VERSIONS } from "../services/consent.js";
import { clearAllClientAuth } from "../lib/authCleanup.js";
import { isPersonaInVoterGeo } from "../utils/phone.js";

/**
 * RWD-7/RWD-8: XP state replaces the old per-persona civics dict.
 *
 *   balance               — spendable XP in the Reward Shop (decremented by
 *                           /api/redemptions when items are purchased)
 *   totalEarned           — cumulative XP ever earned; drives trivia unlocks
 *   triviaPlaysConsumed   — how many trivia plays the voter has spent
 *   triviaPlaysAvailable  — derived: floor(totalEarned / xpPerTriviaPlay)
 *                           minus triviaPlaysConsumed. Never negative.
 *   xpPerTriviaPlay       — backend-owned threshold (10 in migration 033).
 *
 * Everything is per-voter, not per-persona — chatting with five personas
 * contributes to one shared pool. The backend /api/voters/me/xp is the
 * single source of truth; this hook just caches it across re-renders and
 * optimistically bumps it on chat success so the UI feels live.
 */
const DEFAULT_XP_STATE = {
  balance: 0,
  totalEarned: 0,
  triviaPlaysConsumed: 0,
  triviaPlaysAvailable: 0,
  xpPerTriviaPlay: 10,
};

export function useAppState() {
  const [personas, setPersonas] = useState([]);
  const [personaTotal, setPersonaTotal] = useState(0);
  const [personaPage, setPersonaPage] = useState(0);
  const PERSONA_PAGE_SIZE = 20;
  // v1.28.0 (AGCY): the admin Personas list has tabs (Officials | Agencies).
  // Tab state is lifted here so the candidates-fetch effect can re-fire
  // with the right ?type= filter on tab change. Page also resets to 0
  // when the tab changes so pagination doesn't leak across types.
  const [adminPersonaTab, _setAdminPersonaTab] = useState("official");
  const setAdminPersonaTab = (t) => {
    _setAdminPersonaTab(t);
    setPersonaPage(0);
  };
  const [rewards, setRewards] = useState(SHOP_ITEMS);
  const [voter, setVoter] = useState(() => lsGet("qm_voter", null));
  const [admin, setAdmin] = useState(() => ssGet("qm_admin") === "1");
  const [adminAuthed, setAdminAuthed] = useState(() => ssGet("qm_admin") === "1");
  // Persona Owner state (separate from admin — PO gets limited sidebar, not full admin)
  const [po, setPo] = useState(() => ssGet("qm_po") === "1");
  const [poAuthed, setPoAuthed] = useState(() => ssGet("qm_po") === "1");
  const [poPersonaId, setPoPersonaId] = useState(() => ssGet("qm_po_persona_id") || null);
  const [poPersona, setPoPersona] = useState(null);
  // BUG-5: derive initial pending persona route from URL (no setState during render)
  const _initPath = window.location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const _initAdminPersonaMatch = _initPath.startsWith("admin/personas/") && (ssGet("qm_admin") === "1" || ssGet("qm_po") === "1")
    ? (() => { const p = _initPath.split("/"); return p[2] ? { pid: p[2], sub: p[3] } : null; })()
    : null;
  const [pendingPersonaId, setPendingPersonaId] = useState(_initAdminPersonaMatch?.pid ?? null);
  const [poApplication, setPoApplication] = useState(null);
  const _subViewMap = {
    analytics: "analytics", chat: "chat", questions: "questions",
    sessions: "sessions", kb: "kb", tone: "tone",
    statement: "statement", cache: "cache", credits: "credits",
  };
  const [pendingAdminView, setPendingAdminView] = useState(
    _initAdminPersonaMatch ? (_subViewMap[_initAdminPersonaMatch.sub] || "profile") : null
  );
  const [view, setView] = useState(() => {
    const path = _initPath;
    if (path === "admin") return "admin-login";
    if (path === "admin/create" && ssGet("qm_admin") === "1") return "create";
    if (path === "admin/personas" && ssGet("qm_admin") === "1") return "list";
    if (path === "admin/reporting" && ssGet("qm_admin") === "1") return "reporting";
    if (path === "admin/rewards" && ssGet("qm_admin") === "1") return "rewards";
    // BUG-5: admin persona routes — return "list" temporarily, resolved after personas load
    if (_initAdminPersonaMatch) return "list";
    if (path === "admin/analytics" && ssGet("qm_admin") === "1") return "analytics";
    if (path === "admin/applications" && ssGet("qm_admin") === "1") return "applications";
    if (path === "manage") return "admin-login"; // Unified: /manage → /admin login
    if (path === "manage/signup" || path === "admin/signup") return "po-signup";
    if (path === "auth/callback") return "auth-callback";
    if (path === "owner/claim") return "claim-details";
    if (path === "owner/verify") return "claim-verify";
    if (path === "owner/status") return "claim-status";
    if (path === "owner/dashboard" && ssGet("qm_po") === "1") return "po-dashboard";
    if (path === "shop") { const v = lsGet("qm_voter", null); return v ? "shop" : "onboard"; }
    // v1.28.0 (AGCY): voter-facing Agencies directory. Anonymous browsing
    // is supported (same as the Officials directory when there's no voter).
    if (path === "agencies") return "agency-dir";
    if (path === "me") { const v = lsGet("qm_voter", null); return v ? "voter-profile" : "onboard"; }
    if (ssGet("qm_admin") === "1") return "dashboard";
    if (ssGet("qm_po") === "1") return "po-dashboard";
    const v = lsGet("qm_voter", null);
    return v ? "dir" : "onboard";
  });
  const [sel, setSel] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [triviaPersona, setTriviaPersona] = useState(null);
  // v1.28.5 — surfaced when /api/trivia/:personaId/complete fails so the
  // user sees a real error banner instead of silently losing their claim.
  // Consumed by Chat.jsx / ProfilePage.jsx and cleared by the same.
  const [triviaClaimError, setTriviaClaimError] = useState(null);
  // RWD-7/RWD-8: single per-voter XP state, replaces civics dict + voterXp.
  // Seeded from localStorage so the UI renders instantly on reload; the
  // getXpState() effect below reconciles against the authoritative server
  // value once the API is reachable.
  const [xpState, setXpState] = useState(() => {
    const v = lsGet("qm_voter", null);
    const key = v?.phone || v?.email;
    if (!key) return DEFAULT_XP_STATE;
    const cachedBalance = lsGet("qm_xp", {})[key] || 0;
    // We don't cache totalEarned locally (pre-RWD-8 it was per-persona), so
    // on reload the trivia-plays-available count starts at zero and gets
    // corrected as soon as /me/xp responds. Better to under-state than
    // over-state plays (server RPC would reject anyway).
    return { ...DEFAULT_XP_STATE, balance: cachedBalance };
  });
  const [apiOffline, setApiOffline] = useState(false);

  // v1.28.2 (AGCY) — stale-fetch guard for the personas list effect.
  //
  // Symptom: admin lands on Dashboard (view="dashboard") → personas effect
  // fires with adminTypeFilter=undefined → /api/admin/personas with NO
  // ?type= param → returns all 78 rows. Admin clicks Personas → Agencies
  // tab → setAdminPersonaTab("agency") → effect re-fires with
  // adminTypeFilter="agency" → /api/admin/personas?type=agency → returns
  // 0 rows. Two fetches in flight simultaneously. If the Dashboard no-type
  // response comes back LATER than the Agencies response (slow network,
  // DB cache miss, whatever), it overwrites the Agencies empty result
  // with the full 78 officials — so the Agencies tab shows officials
  // with a count of 78.
  //
  // Fix: bump a monotonic epoch counter on every fetch. When a response
  // lands, check whether the epoch that initiated it is still current.
  // If it isn't, the response is stale — discard it. Simpler than
  // AbortController (which apiFetch doesn't thread through) and enough
  // because the only harm from stale responses is last-write-wins races.
  const fetchEpochRef = useRef(0);

  const cur = sel ? personas.find(p => p.id === sel.id) : null;

  // ── Load PO's assigned persona when PO is authenticated ──
  useEffect(() => {
    if (!poAuthed || !poPersonaId) return;
    getCandidate(poPersonaId)
      .then(p => {
        if (p) {
          const mapped = {
            conversations: [], feedback: [],
            suggestedPrompts: p.suggested_prompts ?? p.suggestedPrompts ?? [],
            keyPolicies: p.key_policies ?? p.keyPolicies ?? [],
            ...p,
            accentColor: p.accent_color ?? p.accentColor ?? p.party_color ?? p.partyColor ?? "#3b82f6",
            // v1.28.2 — short name for CTA. personaCtaName() reads either
            // camelCase or snake_case, but normalising here keeps the
            // persona shape consistent with the rest of @qm/types.
            shortName: p.short_name ?? p.shortName ?? null,
            avatar: p.avatar ?? (p.name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()),
            photoUrl: p.photo_url ?? p.photoUrl ?? "",
            geoCountry: p.geo_country ?? p.geoCountry ?? "",
            geoState: p.geo_state ?? p.geoState ?? "",
            geoLga: p.geo_lga ?? p.geoLga ?? "",
            kbCount: p.knowledge_entry_count ?? 0,
          };
          setPoPersona(mapped);
          setSel(mapped);
          setPersonas([mapped]);
        }
      })
      .catch(() => {});
  }, [poAuthed, poPersonaId]);

  // ── Load personas from API (re-fetch when voter changes for location filtering) ──
  // v1.28.0 (AGCY) — type-filter derivation lives at render scope so the
  // derived strings can be proper React deps. The filters go:
  //   • Admin on /admin/personas ("list") → current tab (adminPersonaTab)
  //   • Admin on dashboard / reporting     → undefined (both types returned)
  //   • Voter on /agencies                 → 'agency'
  //   • Voter on Directory (/)             → undefined (backend defaults to 'official')
  const adminTypeFilter = adminAuthed && view === "list"
    ? adminPersonaTab
    : undefined;
  const voterTypeFilter = !adminAuthed && view === "agency-dir" ? "agency" : undefined;

  useEffect(() => {
    if (poAuthed) return; // PO loads their persona separately above
    // Hotfix v1.27.2 — skip fetching candidates while the user is still on
    // the email-entry screen. Before this guard, /api/candidates fired on
    // every anonymous mount of the onboarding view, burning a round-trip
    // that nothing in the UI could consume yet (there's no directory to
    // render until the user has at least started creating a voter record).
    // NOTE: we intentionally still fetch when `voter` exists but is
    // email-only — anonymous browsing past onboarding is a supported path.
    if (view === "onboard" && !voter && !adminAuthed) return;
    // v1.28.0 (AGCY): we do WANT this effect to fire for anonymous voters
    // on /agencies (view === "agency-dir") — the geo-filter + type=agency
    // request can run anonymously and the backend returns [] until the
    // voter onboards, same as the Officials directory does today. No
    // extra guard needed beyond the onboard-view early-return above.
    //
    // v1.28.2 — epoch snapshot. Every time this effect runs we bump the
    // ref and record OUR value. When the promise resolves later we
    // compare — if someone else re-fired the effect in the meantime and
    // bumped the counter past us, discard the response. Prevents the
    // "admin on Dashboard kicks off a no-type fetch, admin clicks
    // Agencies tab which kicks off ?type=agency, Dashboard response
    // lands AFTER Agencies and overwrites the empty Agencies state
    // with all 78 officials" race that was showing stale list + stale
    // count on the Agencies tab.
    const myEpoch = ++fetchEpochRef.current;
    const isStale = () => myEpoch !== fetchEpochRef.current;
    const fetchPersonas = adminAuthed
      ? getAdminPersonas({
          limit: PERSONA_PAGE_SIZE,
          offset: personaPage * PERSONA_PAGE_SIZE,
          personaType: adminTypeFilter,
        }).then(res => {
          // Stale guard on the count update too — same reason.
          if (!isStale() && res?.total != null) setPersonaTotal(res.total);
          return res?.personas ?? res ?? [];
        })
      : getCandidates({ personaType: voterTypeFilter });

    fetchPersonas
      .then(data => {
        if (isStale()) return;
        // v1.28.2 — always clear personas state on empty response. Before
        // this fix, an empty response (e.g. Agencies tab with 0 agencies)
        // left the previous tab's 78 officials in state. `data?.length`
        // was falsy → the entire mapping + setPersonas block was skipped
        // → UI still rendered 78 rows under the Agencies tab. Combined
        // with `total = personaTotal || personas.length`, the count
        // also fell back to personas.length (78) — so the Agencies tab
        // showed "78 agencies · showing 1–20" with Senators listed.
        if (!data?.length) {
          setPersonas([]);
          setApiOffline(false);
          return;
        }
        const mapped = data.map(p => ({
            conversations: [], feedback: [],
            suggestedPrompts: ["What are your top priorities?", "How will you handle the economy?", "What is your stance on healthcare?", "How will you improve education?"],
            keyPolicies: [],
            ...p,
            id: p.id,
            accentColor: p.accent_color ?? p.accentColor ?? p.party_color ?? p.partyColor ?? "#3b82f6",
            // v1.28.2 — short name for CTA. personaCtaName() reads either
            // camelCase or snake_case, but normalising here keeps the
            // persona shape consistent with the rest of @qm/types.
            shortName: p.short_name ?? p.shortName ?? null,
            avatar: p.avatar ?? (p.name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()),
            photoUrl: p.photo_url ?? p.photoUrl ?? "",
            suggestedPrompts: p.suggested_prompts ?? p.suggestedPrompts ?? ["What are your top priorities?", "How will you handle the economy?"],
            keyPolicies: p.key_policies ?? p.keyPolicies ?? [],
            geoCountry: p.geo_country ?? p.geoCountry ?? "",
            geoState: p.geo_state ?? p.geoState ?? "",
            geoLga: p.geo_lga ?? p.geoLga ?? "",
            kbCount: p.kbCount ?? p.knowledge_entry_count ?? 0,
            // CAND-8: admin stats (only present from /api/admin/personas)
            totalSessions: p.totalSessions ?? 0,
            totalQuestions: p.totalQuestions ?? 0,
            uniqueVoters: p.uniqueVoters ?? 0,
            feedbackCounts: p.feedbackCounts ?? { liked: 0, disliked: 0, flagged: 0 },
          }));
          setPersonas(mapped);
          setApiOffline(false);
          // NAV-6: slug routing — supports /:slug, /:slug/chat, and static routes
          const path = window.location.pathname.replace(/^\//, "").replace(/\/$/, "");
          const staticRoutes = [
            "admin", "admin/create", "admin/personas", "admin/analytics", "admin/reporting", "admin/rewards", "admin/applications", "admin/signup",
            "manage", "manage/signup", "owner/claim", "owner/verify", "owner/status", "owner/dashboard",
            "auth/callback",
            // v1.28.3 — "agencies" must be here or the slug-routing fallback
            // picks up the path, fails to find a persona with slug="agencies",
            // and shows the "Profile not found" page. Initial view()
            // resolution at mount correctly maps /agencies → "agency-dir",
            // but then this effect fires on personas load and *also* tries
            // to resolve the path, clobbering the view.
            "shop", "me", "agencies",
          ];
          // BUG-5: skip slug routing for admin persona URLs and persona owner routes
          if (path && !staticRoutes.includes(path) && !path.startsWith("admin/personas/") && !path.startsWith("admin/applications/")) {
            const parts = path.split("/");
            const slug = parts[0];
            const subPath = parts[1]; // "chat" or undefined
            const match = mapped.find(p => p.slug === slug);
            if (match) {
              setSel(match);
              setView(subPath === "chat" ? "chat" : "profile");
            } else {
              // BUG-H8: persona not in geo-filtered list — try public lookup by slug.
              // v1.28.3: the primary list is type-filtered to officials (or
              // agencies if we're on /agencies), so a voter hitting a direct
              // agency URL from the officials directory necessarily lands
              // here even when they ARE in the agency's jurisdiction. Derive
              // outOfGeo from the actual voter/persona geo match instead of
              // blindly flagging true — see isPersonaInVoterGeo in utils/phone.
              getCandidateBySlug(slug)
                .then(persona => {
                  if (persona && persona.id) {
                    const p = {
                      conversations: [], feedback: [],
                      suggestedPrompts: [],
                      keyPolicies: [],
                      ...persona,
                      accentColor: persona.accent_color ?? persona.accentColor ?? persona.party_color ?? persona.partyColor ?? "#3b82f6",
                      avatar: persona.avatar ?? (persona.name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()),
                      photoUrl: persona.photo_url ?? persona.photoUrl ?? "",
                      suggestedPrompts: persona.suggested_prompts ?? persona.suggestedPrompts ?? [],
                      keyPolicies: persona.key_policies ?? persona.keyPolicies ?? [],
                      geoCountry: persona.geo_country ?? persona.geoCountry ?? "",
                      geoState: persona.geo_state ?? persona.geoState ?? "",
                      geoLga: persona.geo_lga ?? persona.geoLga ?? "",
                      kbCount: persona.knowledge_entry_count ?? 0,
                      outOfGeo: !isPersonaInVoterGeo(persona, voter),
                    };
                    // BUG-2 fix: add to personas array so cur resolves (cur = personas.find(…))
                    setPersonas(prev => prev.some(x => x.id === p.id) ? prev : [...prev, p]);
                    setSel(p);
                    // Only allow profile view — chat requires being within geo scope
                    setView("profile");
                  } else {
                    setView("not-found");
                  }
                })
                .catch(() => setView("not-found"));
            }
          }
      })
      .catch(() => setApiOffline(true));
    // Hotfix v1.27.2 — deps deliberately DO NOT include the raw `view`.
    // Adding it re-fired the effect on every intra-authenticated view
    // toggle ("list" → "create" → "list" in the admin create-persona
    // flow), causing an e2e flake when the mock pushed on validateOnly.
    //
    // Hotfix v1.27.3 — voter.country / voter.state / voter.lga added so
    // LocationEditModal's state/LGA save refreshes the directory.
    //
    // v1.28.0 (AGCY): admin switches between Officials and Agencies views
    // need to re-fire the list fetch with a different `?type=` filter.
    // We depend on the DERIVED adminTypeFilter / voterTypeFilter values
    // rather than `view` itself — string equality is the React check,
    // and list→create→list keeps adminTypeFilter as "official" at
    // both ends (only the middle "create" view has it briefly null,
    // which DOES re-fire on the way in AND out — a minor extra fetch
    // but not a regression since v1.27.2's concern was the post-create
    // state being overwritten; adding view-driven filter re-fires
    // here is fine because the mock fix in helpers.js makes the
    // validateOnly POST not push to the fixture list).
  }, [voter?.phone, voter?.email, voter?.country, voter?.state, voter?.lga, adminAuthed, personaPage, adminTypeFilter, voterTypeFilter]);

  // Auto-retry when offline: poll health endpoint every 15s, reload personas on recovery
  useEffect(() => {
    if (!apiOffline) return;
    const healthUrl = `${import.meta.env.VITE_API_URL ?? ""}/health`.replace(/\/\/$/, "/");
    const timer = setInterval(() => {
      fetch(healthUrl).then(r => {
        if (r.ok) { setApiOffline(false); window.location.reload(); }
      }).catch(() => {});
    }, 15_000);
    return () => clearInterval(timer);
  }, [apiOffline]);

  // BUG-5: resolve pending admin/PO persona route after personas load
  useEffect(() => {
    if (!pendingPersonaId || !personas.length || (!adminAuthed && !poAuthed)) return;
    const match = personas.find(p => p.id === pendingPersonaId);
    if (match) {
      setSel(match);
      const targetView = pendingAdminView === "analytics" ? "analytics" : pendingAdminView === "chat" ? "chat" : "profile";
      setView(targetView);
      setPendingPersonaId(null);
      setPendingAdminView(null);
    }
  }, [personas, pendingPersonaId, adminAuthed]);

  // CAND-1: Handle browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname.replace(/^\//, "").replace(/\/$/, "");
      if (!path || path === "") {
        setSel(null);
        setView(admin ? "dashboard" : po ? "po-dashboard" : "dir");
        return;
      }
      // Static routes
      if (path === "admin") { setView("admin-login"); return; }
      if (path === "admin/personas" && adminAuthed) { setView("list"); return; }
      if (path === "auth/callback") { setView("auth-callback"); return; }
      if (path === "manage") { setView("admin-login"); return; } // Unified: /manage → /admin login
      if (path === "manage/signup" || path === "admin/signup") { setView("po-signup"); return; }
      if (path === "owner/claim") { setView("claim-details"); return; }
      if (path === "owner/verify") { setView("claim-verify"); return; }
      if (path === "owner/status") { setView("claim-status"); return; }
      if (path === "owner/dashboard" && poAuthed) { setView("po-dashboard"); return; }
      if (path === "admin/create" && adminAuthed) { setView("create"); return; }
      if (path === "admin/reporting" && adminAuthed) { setView("reporting"); return; }
      if (path === "admin/rewards" && adminAuthed) { setView("rewards"); return; }
      if (path === "admin/applications" && adminAuthed) { setView("applications"); return; }
      if (path.startsWith("admin/applications/") && adminAuthed) { setView("application-detail"); return; }
      // BUG-5: admin/PO persona routes on back/forward
      if (path.startsWith("admin/personas/") && (adminAuthed || poAuthed)) {
        const parts = path.split("/");
        const pid = parts[2];
        const sub = parts[3];
        const match = personas.find(p => p.id === pid);
        if (match) {
          setSel(match);
          const subViewMap = {
            analytics: "analytics", chat: "chat", questions: "questions",
            sessions: "sessions", kb: "kb", tone: "tone",
            statement: "statement", cache: "cache", credits: "credits",
          };
          setView(subViewMap[sub] || "profile");
        } else {
          setView("list");
        }
        return;
      }
      if (path === "admin/analytics" && adminAuthed) { setView("analytics"); return; }
      if (path === "shop") { setView(voter ? "shop" : "onboard"); return; }
      if (path === "me") { setView(voter ? "voter-profile" : "onboard"); return; }
      // v1.28.3 — /agencies is a public static route (voter Agencies
      // directory). Missing here meant back/forward onto /agencies fell
      // through to slug-matching and showed "Profile not found".
      if (path === "agencies") { setView("agency-dir"); return; }
      // Persona slug routes
      const parts = path.split("/");
      const slug = parts[0];
      const subPath = parts[1];
      const match = personas.find(p => p.slug === slug);
      if (match) {
        setSel(match);
        // BUG-2 fix: out-of-geo personas in history must stay on profile (no chat)
        setView(match.outOfGeo ? "profile" : (subPath === "chat" ? "chat" : "profile"));
      } else {
        // BUG-2 fix: try public lookup for personas outside the currently-
        // loaded type-filtered list on back/forward. v1.28.3: use the real
        // geo-match predicate for outOfGeo — parity with the personas-fetch
        // fallback above so agencies a voter IS eligible for don't get
        // wrongly flagged "out of jurisdiction" after back/forward nav.
        getCandidateBySlug(slug)
          .then(persona => {
            if (persona && persona.id) {
              const p = {
                conversations: [], feedback: [],
                suggestedPrompts: [],
                keyPolicies: [],
                ...persona,
                accentColor: persona.accent_color ?? persona.accentColor ?? persona.party_color ?? persona.partyColor ?? "#3b82f6",
                avatar: persona.avatar ?? (persona.name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()),
                photoUrl: persona.photo_url ?? persona.photoUrl ?? "",
                suggestedPrompts: persona.suggested_prompts ?? persona.suggestedPrompts ?? [],
                keyPolicies: persona.key_policies ?? persona.keyPolicies ?? [],
                geoCountry: persona.geo_country ?? persona.geoCountry ?? "",
                geoState: persona.geo_state ?? persona.geoState ?? "",
                geoLga: persona.geo_lga ?? persona.geoLga ?? "",
                kbCount: persona.knowledge_entry_count ?? 0,
                outOfGeo: !isPersonaInVoterGeo(persona, voter),
              };
              setPersonas(prev => prev.some(x => x.id === p.id) ? prev : [...prev, p]);
              setSel(p);
              setView("profile");
            } else {
              setView("not-found");
            }
          })
          .catch(() => setView("not-found"));
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [personas, admin]);

  // ── Load rewards from API on mount ────────────────────────────────────────
  // Hotfix v1.27.2 — don't fetch /api/rewards on the email-entry screen.
  // Before the guard, this effect ran unconditionally on mount (deps []),
  // so every anonymous page load burned a rewards round-trip the UI
  // couldn't consume yet (Reward Shop is voter-only). The SHOP_ITEMS
  // fallback already covers the anonymous render path. We fetch once
  // view has moved past onboarding, or a voter / admin session exists.
  useEffect(() => {
    if (view === "onboard" && !voter && !adminAuthed) return;
    getRewards()
      .then(data => {
        if (data?.length) setRewards(data.map(r => ({
          id: r.legacy_id ?? r.id,
          name: r.name,
          icon: r.icon ?? "🎁",
          cost: r.xp_cost ?? r.cost,
          cat: r.category ?? r.cat ?? "Other",
          provider: r.provider ?? "",
          desc: r.description ?? r.desc ?? "",
          active: r.active ?? true,
        })).filter(r => r.active));
      })
      .catch(() => { /* keep SHOP_ITEMS */ });
    // Deps deliberately DO NOT include `view` — same reasoning as the
    // candidates-fetch effect above. Progressing past the onboarding
    // screen requires either voter.phone/email or adminAuthed to flip,
    // which already triggers a re-fire of this effect. Adding `view`
    // would cause re-fetches on every intra-authenticated view toggle
    // (Shop → Chat → Shop, etc.) which is wasted work — the reward
    // catalogue doesn't change mid-session.
  }, [voter?.phone, voter?.email, adminAuthed]);

  // ── Load unified XP state from /me/xp when voter is set ─────────────────
  // RWD-7/RWD-8: replaces the old dual getCivics + getXp phone-keyed reads.
  // JWT-scoped, so no phone needed — the backend resolves voter_id from
  // the bearer token. Runs for every voter (email-only included).
  useEffect(() => {
    if (!voter?.phone && !voter?.email) return;
    getXpState()
      .then(data => {
        if (!data) return;
        setXpState({
          balance: data.balance ?? 0,
          totalEarned: data.totalEarned ?? 0,
          triviaPlaysConsumed: data.triviaPlaysConsumed ?? 0,
          triviaPlaysAvailable: data.triviaPlaysAvailable ?? 0,
          xpPerTriviaPlay: data.xpPerTriviaPlay ?? 10,
        });
      })
      .catch(() => {});
  }, [voter?.phone, voter?.email]);

  const addPersona = p => { setPersonas(prev => [...prev, p]); setPersonaTotal(prev => prev + 1); setView("list"); };
  const updatePersona = p => setPersonas(prev => prev.map(x => x.id === p.id ? { ...x, ...p } : x));
  const updateConv = (pid, conv) => setPersonas(prev =>
    prev.map(p => p.id !== pid ? p : { ...p, conversations: [...(p.conversations || []).filter(c => c.id !== conv.id), conv] })
  );
  const addFb = (pid, mid, type) => setPersonas(prev =>
    prev.map(p => p.id !== pid ? p : { ...p, feedback: [...(p.feedback || []).filter(f => f.msgId !== mid), { msgId: mid, type, timestamp: now() }] })
  );
  const navBack = () => {
    if (view === "chat" && cur) {
      // BUG-5: admin goes to /admin/personas/:id, voter goes to /:slug
      if (admin) {
        window.history.pushState({}, "", `/admin/personas/${cur.id}`);
      } else {
        window.history.pushState({}, "", `/${cur.slug}`);
      }
      setView("profile");
      return;
    }
    window.history.pushState({}, "", "/");
    setView(admin ? "list" : "dir");
    setSel(null);
  };

  const handleAdminLogout = async () => {
    // AUTH-15: admin logout must land on /admin (the login screen), NOT on
    // the voter directory or onboarding. Without the URL push the next
    // apiFetch()/apiFetchAdmin() 401 redirect would look at the wrong path.
    //
    // Hotfix v1.26.4: centralised cleanup. Before this we only cleared the
    // admin sessionStorage flag — the Supabase JWT lived on, so "logout"
    // left a signed-in admin who couldn't see the admin tab but still had
    // full API access via Bearer token. clearAllClientAuth wipes Supabase
    // session + legacy sb-*-auth-token keys + voter/admin/PO flags in one
    // shot, matching what the voter and PO logout paths now do.
    await clearAllClientAuth();
    setAdminAuthed(false);
    setAdmin(false);
    // Copilot #3 — reset in-memory voter state alongside the storage wipe.
    // Before this, clearAllClientAuth() cleared localStorage.qm_voter but
    // the React `voter` state stayed truthy until a reload, so effects
    // that read `voter` (e.g. the /candidates fetcher at line 149) could
    // still fire authenticated requests with a voter object whose backing
    // JWT had just been revoked.
    setVoter(null);
    setXpState(DEFAULT_XP_STATE);
    setPersonas([]);
    window.history.pushState({}, "", "/admin");
    setView("admin-login");
    setSel(null);
  };

  const handleAdminLoginSuccess = () => {
    ssSet("qm_admin", "1");
    setAdminAuthed(true);
    setAdmin(true);
    // AUTH-16: honour ?next= if apiFetch bounced the admin here with a
    // return target. Full reload so the hash + query survive and the
    // initial view() resolver picks the right view for the deep link.
    //
    // Hotfix v1.27.3 — loop guard. If `next` is `/admin` itself (or
    // another `/admin?next=…` chain), honouring it just reloads us on
    // the same login page → admin-only-views effect or the adminAuth
    // middleware rejects → another wipe-and-redirect → another loop.
    // Skip and go to the dashboard instead. Also reject any `next` that
    // doesn't start with `/admin` (AUTH-16 open-redirect guard).
    const params = new URLSearchParams(window.location.search);
    const next = params.get("next");
    const nextPath = next ? next.split("?")[0] : "";
    const isSafeNext =
      next &&
      next.startsWith("/admin") &&
      // Bare `/admin` is the login path — never a useful post-login destination.
      nextPath !== "/admin";
    if (isSafeNext) {
      window.location.href = next;
      return;
    }
    window.history.pushState({}, "", "/");
    setView("list");
    setSel(null);
  };

  // ── Persona Owner login/logout ──────────────────────────────────────
  const handlePoLoginSuccess = async ({ personaId, email: _email }) => {
    ssSet("qm_po", "1");
    if (personaId) ssSet("qm_po_persona_id", personaId);
    setPo(true);
    setPoAuthed(true);
    setPoPersonaId(personaId);

    if (personaId) {
      // Approved PO with linked persona → go to PO dashboard
      window.history.pushState({}, "", "/owner/dashboard");
      setView("po-dashboard");
    } else {
      // No persona linked yet → check for existing application
      try {
        const app = await getApplication();
        if (app) {
          setPoApplication(app);
          // Route based on application status
          const status = app.status;
          if (status === "draft" || status === "pending_details") {
            window.history.pushState({}, "", "/owner/claim");
            setView("claim-details");
          } else if (status === "pending_verification") {
            window.history.pushState({}, "", "/owner/verify");
            setView("claim-verify");
          } else {
            window.history.pushState({}, "", "/owner/status");
            setView("claim-status");
          }
        } else {
          // No application exists — start the claim flow
          window.history.pushState({}, "", "/owner/claim");
          setView("claim-details");
        }
      } catch {
        // API error — default to claim start
        window.history.pushState({}, "", "/owner/claim");
        setView("claim-details");
      }
    }
  };

  const handlePoLogout = async () => {
    // Hotfix v1.26.4: route through the shared cleanup helper. Before this
    // we called supabase.auth.signOut() but skipped purgeAllSupabaseSessions,
    // so legacy sb-*-auth-token keys (pre-v1.23.0 storageKey migration)
    // survived and resurrected the session on the next page load.
    await clearAllClientAuth();
    setPo(false);
    setPoAuthed(false);
    setPoPersonaId(null);
    setPoPersona(null);
    setSel(null);
    // Copilot #3 — reset in-memory voter state too. clearAllClientAuth()
    // wipes localStorage.qm_voter, but the React `voter` state is only
    // initialised from localStorage at mount. Until we reset it here,
    // effects reading `voter` would still see the pre-logout value and
    // could fire authenticated fetches against a revoked JWT.
    setVoter(null);
    setXpState(DEFAULT_XP_STATE);
    setPersonas([]);
    window.history.pushState({}, "", "/");
    setView("onboard");
  };

  // RWD-7/RWD-8: chat-earned XP. Backend already wrote the increment via the
  // increment_xp RPC; this is the optimistic local update so the header
  // XP badge + trivia-play count refresh immediately. Derivation mirrors the
  // server: triviaPlaysAvailable = floor(totalEarned / xpPerTriviaPlay) − consumed.
  const handleXpEarned = (xpEarned) => {
    if (!xpEarned || xpEarned <= 0) return;
    setXpState(prev => {
      const perPlay = prev.xpPerTriviaPlay || 10;
      const totalEarned = (prev.totalEarned || 0) + xpEarned;
      const balance = (prev.balance || 0) + xpEarned;
      const triviaPlaysAvailable = Math.max(
        0,
        Math.floor(totalEarned / perPlay) - (prev.triviaPlaysConsumed || 0)
      );
      const key = voter?.phone || voter?.email;
      if (key) {
        const stored = lsGet("qm_xp", {}); stored[key] = balance; lsSet("qm_xp", stored);
      }
      return { ...prev, totalEarned, balance, triviaPlaysAvailable };
    });
  };

  // RWD-7/RWD-8: trivia consumption is the backend's concern. The TriviaModal
  // calls POST /api/trivia/:personaId, which gates on consume_trivia_play
  // (atomic decrement under row lock). We DO NOT decrement locally — the RPC
  // is the only source of truth so concurrent requests can't double-spend.
  // Refresh from /me/xp when the modal closes so consumed count reconciles.
  const handlePlayTrivia = (persona) => {
    if ((xpState.triviaPlaysAvailable || 0) <= 0) return;
    setTriviaPersona(persona);
  };

  // v1.28.5 — take the server's fresh XpState directly as the source of truth.
  //
  // Pre-v1.28.5 flow:
  //   1. TriviaModal called /complete, got back { xpEarned, balance, ... }
  //   2. Extracted just xpEarned, dropped the rest, and called this with
  //      the raw number
  //   3. This fn did an optimistic local bump, then FIRED a second
  //      /me/xp read to reconcile
  //
  // The reconcile step was the bug: when /complete errored (e.g. round
  // token missing, 409 replay, 500 from a missing migration), the modal
  // fell back to the client-side score. The optimistic bump used the
  // client score; the /me/xp read showed the real, UN-incremented server
  // state; the UI "rolled back" the claim and XP appeared to not add.
  //
  // v1.28.5 flow: TriviaModal passes the full serverResult (or null if
  // /complete failed). If serverResult is present, it IS the authoritative
  // state — apply it directly, skip the extra round-trip. If null, surface
  // an error toast so the user knows to retry instead of silently losing
  // the claim.
  const handleTriviaXpEarned = (serverResult) => {
    setTriviaPersona(null);

    if (serverResult && typeof serverResult === "object") {
      // Server returned authoritative state. Use it verbatim — no second
      // /me/xp read, no optimistic math that could drift.
      setXpState({
        balance: serverResult.balance ?? 0,
        totalEarned: serverResult.totalEarned ?? 0,
        triviaPlaysConsumed: serverResult.triviaPlaysConsumed ?? 0,
        triviaPlaysAvailable: serverResult.triviaPlaysAvailable ?? 0,
        xpPerTriviaPlay: serverResult.xpPerTriviaPlay ?? 10,
      });
      const key = voter?.phone || voter?.email;
      if (key) {
        const stored = lsGet("qm_xp", {});
        stored[key] = serverResult.balance ?? 0;
        lsSet("qm_xp", stored);
      }
      return;
    }

    // /complete failed — network, 4xx, 500. Don't mutate XP state (client
    // has no trustworthy delta). Surface a toast so the user can retry.
    setTriviaClaimError(
      "Couldn't save your XP. Reload and try again — your round is preserved.",
    );
    // Still re-read /me/xp in case the server DID commit but the response
    // got lost on the wire — this reconciles opportunistically.
    getXpState().then(data => {
      if (!data) return;
      setXpState({
        balance: data.balance ?? 0,
        totalEarned: data.totalEarned ?? 0,
        triviaPlaysConsumed: data.triviaPlaysConsumed ?? 0,
        triviaPlaysAvailable: data.triviaPlaysAvailable ?? 0,
        xpPerTriviaPlay: data.xpPerTriviaPlay ?? 10,
      });
    }).catch(() => {});
  };

  // Reward Shop purchase. /api/redemptions is what actually moves balance
  // server-side; here we just shadow it so the UI feels responsive.
  const handleSpendXp = (cost) => {
    setXpState(prev => {
      const balance = Math.max(0, (prev.balance || 0) - cost);
      const key = voter?.phone || voter?.email;
      if (key) {
        const stored = lsGet("qm_xp", {}); stored[key] = balance; lsSet("qm_xp", stored);
      }
      return { ...prev, balance };
    });
  };

  const handleOnboardComplete = async (voterData) => {
    setVoter(voterData);
    if (voterData) {
      lsSet("qm_voter", voterData);
      const voterKey = voterData.phone || voterData.email;
      // RWD-7/RWD-8: seed balance from cached qm_xp; totalEarned/plays will
      // arrive from /me/xp below. Keeps onboard snappy on slow networks.
      const savedXp = lsGet("qm_xp", {});
      setXpState({
        ...DEFAULT_XP_STATE,
        balance: voterKey ? (savedXp[voterKey] || 0) : 0,
      });
      // Persist voter to API then load server-side state + record consents
      upsertVoter(voterData)
        .then((upserted) => {
          // JWT-scoped — no phone/email needed; server uses auth_id.
          getXpState().then(d => {
            if (!d) return;
            setXpState({
              balance: d.balance ?? 0,
              totalEarned: d.totalEarned ?? 0,
              triviaPlaysConsumed: d.triviaPlaysConsumed ?? 0,
              triviaPlaysAvailable: d.triviaPlaysAvailable ?? 0,
              xpPerTriviaPlay: d.xpPerTriviaPlay ?? 10,
            });
          }).catch(() => {});
          // LEGAL-3: Record policy consent if voter consented during onboarding
          if (voterData.consents?.policyConsent && upserted?.id) {
            recordConsents([
              { voterId: upserted.id, policyType: "privacy_policy", policyVersion: POLICY_VERSIONS.privacy_policy, action: "grant" },
              { voterId: upserted.id, policyType: "terms_of_service", policyVersion: POLICY_VERSIONS.terms_of_service, action: "grant" },
            ]).catch(() => {});
          }
        })
        .catch(() => {});
      if (voterData.phone) migrateLocalStorageToDb(voterData.phone);
    }
    // VOTER-5: Check for a return URL (e.g., from chat-requires-login redirect)
    const returnUrl = sessionStorage.getItem("qm_return_url");
    if (returnUrl) {
      sessionStorage.removeItem("qm_return_url");
      window.history.pushState({}, "", returnUrl);
      // Parse slug from return URL to set selected persona
      const slug = returnUrl.replace(/^\//, "").split("/")[0];
      const match = personas.find(p => p.slug === slug);
      if (match) {
        setSel(match);
        setView("chat");
        return;
      }
    }
    setView("dir");
  };

  // EMAIL-5: Called after magic link verification — transitions to location step or directory
  const handleMagicLinkVerified = (email, existingVoter) => {
    // EMAIL-15: If returning user with complete profile, skip onboarding entirely
    // Require both state and country to consider onboarding complete
    if (existingVoter?.state && existingVoter?.country) {
      const voterData = {
        email: existingVoter.email ?? email,
        phone: existingVoter.phone ?? undefined,
        authId: existingVoter.auth_id ?? undefined,
        countryCode: existingVoter.country_code ?? "",
        country: existingVoter.country ?? "",
        flag: existingVoter.flag ?? "",
        state: existingVoter.state ?? "",
        lga: existingVoter.lga ?? "",
        gender: existingVoter.gender ?? "",
      };
      handleOnboardComplete(voterData);
      return;
    }
    // New user — store verified email and go to location selection
    sessionStorage.setItem("qm_verified_email", email);
    window.history.pushState({}, "", "/");
    setView("onboard");
  };

  const handleVoterUpdate = (updatedVoter) => {
    setVoter(updatedVoter);
    lsSet("qm_voter", updatedVoter);
  };

  const handleResetVoter = async () => {
    // Hotfix v1.26.4: voter "reset" used to only wipe qm_voter + friends
    // in localStorage. The Supabase JWT survived, so the next visit to /
    // resolved to "onboard" with no voter row but an authenticated session
    // — the /api/candidates call leaked the full list (see candidates.ts
    // geo-gate fix) and /me/voter 401'd. clearAllClientAuth() brings this
    // logout path in line with admin/PO logout + the apiFetch 401 handler.
    await clearAllClientAuth();
    setVoter(null);
    setXpState(DEFAULT_XP_STATE);
    setSel(null);
    window.history.pushState({}, "", "/");
    setView("onboard");
  };

  useEffect(() => {
    // Admin-only views: kick non-admin, non-PO users out
    const adminOnlyViews = ["list", "create", "rewards", "dashboard", "applications", "application-detail"];
    const personaViews = ["analytics", "questions", "sessions", "kb", "tone", "statement", "cache", "credits", "profile", "chat"];
    if (!adminAuthed && !poAuthed && adminOnlyViews.includes(view)) {
      setAdmin(false); setView(voter ? "dir" : "onboard");
    }
    // PO can access persona sub-pages but not admin-only views
    if (poAuthed && !adminAuthed && adminOnlyViews.includes(view)) {
      setView("po-dashboard");
    }
    // VOTER-5: profile and not-found are public; chat requires login (stores return URL)
    // v1.28.0 (AGCY): `agency-dir` joins the public views so anonymous
    // voters can browse the Agencies directory before onboarding (same
    // behaviour as the Officials directory / the "dir" view).
    const publicViews = ["onboard", "auth-callback", "admin-login", "profile", "not-found", "po-login", "po-signup", "claim-details", "claim-verify", "claim-status", "po-dashboard", "agency-dir"];
    if (!voter && !adminAuthed && !poAuthed && !publicViews.includes(view)) {
      if (view === "chat" && sel) {
        // Store return URL so onboarding can redirect back after login
        sessionStorage.setItem("qm_return_url", `/${sel.slug}/chat`);
      }
      setView("onboard");
    }
  }, [view, adminAuthed, poAuthed, voter]);

  return {
    personas, rewards, voter, admin, adminAuthed, view, setView, sel, setSel,
    triviaPersona, setTriviaPersona, cur, apiOffline,
    // v1.28.5 — user-visible error banner when /complete fails.
    triviaClaimError, setTriviaClaimError,
    personaTotal, personaPage, setPersonaPage, PERSONA_PAGE_SIZE,
    // v1.28.0 (AGCY) — admin Personas list tab state (Officials | Agencies)
    adminPersonaTab, setAdminPersonaTab,
    // RWD-7/RWD-8: XP state + derived conveniences. Components that only
    // need the balance can read `voterXp`; components that need the full
    // shape (Chat header, trivia button) can read `xpState`.
    xpState,
    voterXp: xpState.balance,
    triviaPlaysAvailable: xpState.triviaPlaysAvailable,
    addPersona, updatePersona, updateConv, addFb, navBack,
    handleAdminLogout, handleAdminLoginSuccess,
    // Chat → XP award (replaces handleCivicsUpdate). Trivia → XP claim has
    // its own handler because it also increments triviaPlaysConsumed.
    handleXpEarned, handleTriviaXpEarned,
    handlePlayTrivia, handleSpendXp, handleOnboardComplete,
    handleMagicLinkVerified, handleVoterUpdate, handleResetVoter,
    // Persona Owner state
    po, poAuthed, poPersona, poPersonaId,
    handlePoLoginSuccess, handlePoLogout,
    poApplication, setPoApplication,
    selectedApplication, setSelectedApplication,
  };
}
